/**
 * The board, and the open card (ADR-111, ADR-116).
 *
 * Two things this screen shows that a tracker's own board does not:
 *
 * **Which cards agents can see.** A card without the team marker is visible to
 * a human and invisible to every agent (AC-007). That is a useful state — a
 * draft, a note, something parked — but silently ignoring a card is the most
 * confusing failure a person can hit, so unmanaged cards are marked, not hidden.
 *
 * **What actually happened to a card.** Claims, evidence and transitions are
 * appended to the same thread as human comments, attributed and translated out
 * of the platform's own vocabulary — and below the thread, the run itself:
 * stages, attempts and the read-back barrier. That used to be a separate
 * top-level screen reached from a separate list, which put the answer to "what
 * happened to this?" two navigations away from the thing it happened to
 * (ADR-118).
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useMatch, useNavigate, useParams, useSearchParams } from 'react-router';

import type {
  BoardAttachmentView,
  BoardCardView,
  BoardChangeLinkView,
  BoardCommentView,
  HumanReviewRequestView,
  WorkflowView,
} from '../../client/types.js';
import { formatTimestamp } from '../../logic/format.js';
import type { Tone } from '../../logic/display-matrix.js';
import { externalReleaseLinkProps } from '../../logic/releases.js';
import { commentSummary, describeTechnicalComment, isTechnicalComment, withoutAutomationMarker } from '../../logic/task-overview.js';
import { projectWorkflowTasks, workflowTaskPageLoadState } from '../../logic/workflow-task-order.js';
import { useBoard, useConsoleMutation, useHumanReviews, useReleaseConfiguration, useReleasePlans, useRoles, useTaskChecklists, useTeamWorkflow } from '../queries.js';
import { RunTimeline } from '../components/run-timeline.js';
import { useAttachment } from '../components/attachment.js';
import { AttachmentDialog, AttachmentTile } from '../components/preview.js';
import { CommentComposer, type CommentDraft } from '../components/comment-composer.js';
import { Chip, QueryPhase } from '../components/state.js';
import { useStore } from '../store-context.js';
import { Button, Flash } from '@primer/react';
import { GitBranchIcon, PaperclipIcon } from '@primer/octicons-react';

import { BeforeAfterCompare } from '../components/compare.js';
import { AgentCardLabels } from '../components/agent-card-labels.js';
import { BoardReleaseOperations } from '../components/board-release-operations.js';
import { WorkflowGraphBoard } from '../components/workflow-graph-board.js';
import { TaskConversation } from './operator.js';

const PRIORITY_TONES: Readonly<Record<string, Tone>> = {
  P0: 'danger',
  P1: 'wait',
  P2: 'active',
  P3: 'unknown',
};

/* «P1» is a slug; a person reads «High». The stored value stays the slug. */
const PRIORITY_LABELS: Readonly<Record<string, string>> = {
  P0: 'Urgent',
  P1: 'High',
  P2: 'Medium',
  P3: 'Low',
};

/* «ready-for-development» is a workflow id; the column reads as words. */
function columnTitle(id: string): string {
  const words = id.replaceAll('-', ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function BoardScreen(): ReactNode {
  const store = useStore();
  const board = useBoard();
  const roles = useRoles();
  const workflow = useTeamWorkflow();
  const { id: openId = null } = useParams();
  const taskChatOpen = useMatch('/board/:id/chat') !== null;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [composing, setComposing] = useState(false);
  const [showWorkflow, setShowWorkflow] = useState(openId === null);
  const [stageFilter, setStageFilter] = useState<string | null>(() => searchParams.get('stage'));
  const previousOpenId = useRef(openId);
  /*
   * Sticky per browser, not per team: «I want to see done today» is a way of
   * looking at the board, not a change to it — the team document only says
   * which columns start out of sight.
   */
  const [showHidden, setShowHidden] = useState(() => localStorage.getItem('board.show-hidden') === '1');

  /*
   * The open card is in the URL, not in component state.
   *
   * A card is the unit of work here, so it is the thing somebody sends a link
   * to — "look at this one" — and it is where Team and the activity log point
   * when they name a task. Keeping it in state made all three impossible.
   */
  const open = (externalItemId: string | null): void => {
    setShowWorkflow(externalItemId === null);
    void navigate(externalItemId === null ? '/board' : `/board/${encodeURIComponent(externalItemId)}`);
  };

  useEffect(() => {
    if (openId === null) setShowWorkflow(true);
    else if (previousOpenId.current !== openId) setShowWorkflow(false);
    previousOpenId.current = openId;
  }, [openId]);

  useEffect(() => {
    const currentGraph = workflow.data?.graph;
    if (stageFilter !== null && currentGraph !== undefined &&
        (currentGraph === null || !currentGraph.nodes.some((node) => node.id === stageFilter))) {
      setStageFilter(null);
    }
  }, [stageFilter, workflow.data?.graph]);

  const roleNames = useMemo(
    () => new Map((roles.data ?? []).map((role) => [role.roleInstanceId, role.displayName] as const)),
    [roles.data],
  );

  // Drag state lives here, not in the DOM: the source card dims, the column
  // under the pointer glows, and a drop opens the move dialog.
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropColumn, setDropColumn] = useState<string | null>(null);
  /*
   * Moving a card costs words (the rule agents live under too): the dialog
   * shows the open question when there is one, and the comment is mandatory
   * either way — it lands on the card, then the card moves.
   */
  const [pendingMove, setPendingMove] = useState<{ id: string; title: string; to: string } | null>(null);
  const decisions = useHumanReviews();

  useEffect(() => {
    if (openId === null || board.data === undefined || board.data.cards.some((card) => card.externalItemId === openId) ||
        !board.hasNextPage || board.isFetchingNextPage) return;
    void board.fetchNextPage();
  }, [board.data, board.fetchNextPage, board.hasNextPage, board.isFetchingNextPage, openId]);

  useEffect(() => {
    if (!taskChatOpen || openId === null || board.data === undefined || board.isFetchingNextPage) return;
    const routeCard = board.data.cards.find((card) => card.externalItemId === openId);
    if (routeCard === undefined) {
      if (!board.hasNextPage) void navigate('/board', { replace: true });
      return;
    }
    if (!routeCard.managed || routeCard.taskBindingId == null) {
      void navigate(`/board/${encodeURIComponent(openId)}`, { replace: true });
    }
  }, [board.data, board.hasNextPage, board.isFetchingNextPage, navigate, openId, taskChatOpen]);

  const move = useConsoleMutation<{ id: string; to: string; comment: string }, void>(
    async (data, context, args) => {
      await data.commentOnCard(args.id, args.comment, context);
      await data.editCard(args.id, { workflowState: args.to }, context);
    },
    [['board'], ['tasks'], ['human-reviews']],
  );

  const requestMove = (card: { externalItemId: string; title: string; workflowState: string }, to: string): void => {
    if (to === card.workflowState) return;
    setPendingMove({ id: card.externalItemId, title: card.title, to });
  };

  const blocking = (
    <QueryPhase isPending={board.isPending} error={board.error} source="board" onRetry={() => void board.refetch()} />
  );
  const readPhase = boardReadPhase(board.data, board.error);
  if (readPhase === 'blocking') return blocking;

  const data = board.data;
  if (data === undefined) return null;
  const selected = data.cards.find((card) => card.externalItemId === openId) ?? null;
  const taskBindingId = selected?.taskBindingId ?? null;
  const taskChatEligible = taskChatOpen && selected?.managed === true && taskBindingId !== null;
  const hidden = new Set(data.hiddenColumns ?? []);
  const columns = data.columns.filter((column) => showHidden || !hidden.has(column));
  const totalCardCount = data.totalCards ?? data.cards.length;
  const graphlessTaskChat = taskChatEligible && workflow.data?.graph == null;

  return (
    <div
      className={`stack board-screen${workflow.data?.graph != null && openId !== null && !showWorkflow && !composing ? ' board-screen-tasks' : ''}${taskChatEligible ? ' board-screen-task-chat' : ''}`}
    >
      {readPhase === 'stale' ? (
        <Flash variant="warning" role="status">
          The board could not refresh. Task Chat and the last loaded task details remain available.{' '}
          <Button size="small" onClick={() => void board.refetch()}>Retry board</Button>
        </Flash>
      ) : null}
      {graphlessTaskChat ? null : composing ? (
        <NewCard columns={data.columns} onDone={() => setComposing(false)} />
      ) : (
        <div className="board-toolbar">
          <Button variant="primary" onClick={() => setComposing(true)}>
            New card
          </Button>
          <p className="board-count">
            {totalCardCount} card{totalCardCount === 1 ? '' : 's'} · {columns.length} columns
          </p>
          {hidden.size > 0 ? (
            <label className="check board-show-hidden">
              <input
                type="checkbox"
                checked={showHidden}
                onChange={(event) => {
                  setShowHidden(event.target.checked);
                  localStorage.setItem('board.show-hidden', event.target.checked ? '1' : '0');
                }}
              />
              Show hidden ({hidden.size})
            </label>
          ) : null}
        </div>
      )}

      {workflow.data?.graph === null || workflow.data?.graph === undefined ? (
      graphlessTaskChat && selected !== null && taskBindingId !== null ? (
        <section className="task-workbench task-workbench-chat" aria-label="Tasks">
          <GraphlessTaskBrowser
            cards={data.cards}
            total={totalCardCount}
            selectedCardId={openId}
            hasMore={board.hasNextPage}
            onLoadMore={board.fetchNextPage}
            onOpen={open}
          />
          <div className="task-workbench-detail">
            <TaskChatView card={selected} taskBindingId={taskBindingId} />
          </div>
        </section>
      ) : (
      <section className="board-columns" aria-label="Board columns">
        {columns.map((column) => {
          const cards = data.cards.filter((card) => card.workflowState === column);
          return (
            <div
              className={[
                'board-column',
                cards.length === 0 ? 'board-column-empty' : '',
                dropColumn === column ? 'drop-target' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              key={column}
              onDragOver={(event) => {
                // preventDefault is what makes the column a legal drop target.
                event.preventDefault();
                if (dropColumn !== column) setDropColumn(column);
              }}
              onDragLeave={(event) => {
                if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
                setDropColumn((current) => (current === column ? null : current));
              }}
              onDrop={(event) => {
                event.preventDefault();
                const id = event.dataTransfer.getData('text/plain');
                setDropColumn(null);
                setDraggingId(null);
                const dropped = data.cards.find((candidate) => candidate.externalItemId === id);
                if (id.length === 0 || dropped === undefined) return;
                requestMove(dropped, column);
              }}
            >
              <h3 className="board-column-head">
                <span className="board-column-name">{columnTitle(column)}</span>
                <span className="board-column-count">{cards.length}</span>
              </h3>
              {cards.map((card) => (
                <article
                  className={[
                    card.managed ? 'board-card' : 'board-card board-card-unmanaged',
                    draggingId === card.externalItemId ? 'dragging' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  key={card.externalItemId}
                  draggable
                  onDragStart={(event) => {
                    event.dataTransfer.setData('text/plain', card.externalItemId);
                    event.dataTransfer.effectAllowed = 'move';
                    setDraggingId(card.externalItemId);
                  }}
                  onDragEnd={() => {
                    setDraggingId(null);
                    setDropColumn(null);
                  }}
                >
                  <AgentCardLabels labels={card.agentLabels} compact />
                  <header className="board-card-head">
                    <button type="button" className="link-button" onClick={() => open(card.externalItemId)}>
                      {card.title}
                    </button>
                    {card.priority === null ? null : (
                      <Chip tone={PRIORITY_TONES[card.priority] ?? 'unknown'} text={PRIORITY_LABELS[card.priority] ?? card.priority} />
                    )}
                  </header>
                  {card.attachments.length === 0 ? null : (
                    <p className="board-card-files" aria-label={`${String(card.attachments.length)} attached file${card.attachments.length === 1 ? '' : 's'}`}>
                      <PaperclipIcon size={16} />
                      {card.attachments.length}
                    </p>
                  )}
                  {card.changeLinks.length === 0 ? null : (
                    <p className="board-card-change" aria-label={`branch and merge request open`}>
                      <GitBranchIcon size={16} />
                      {card.changeLinks
                        .map((change) => `${repoShort(change.repositoryUrl)} ${changeRef(change)}`)
                        .join(', ')}
                    </p>
                  )}
                  <BoardReleaseOperations operations={card.releaseOperations} compact />
                  <label className="board-card-move">
                    <span className="sr-only">Move {card.title} to</span>
                    <select
                      value={card.workflowState}
                      onChange={(event) => requestMove(card, event.target.value)}
                    >
                      {data.columns.map((option) => (
                        <option value={option} key={option}>
                          {columnTitle(option)}
                        </option>
                      ))}
                    </select>
                  </label>
                </article>
              ))}
              {cards.length === 0 ? <p className="board-column-empty-note">Cards appear here when they reach «{columnTitle(column)}»</p> : null}
            </div>
          );
        })}
      </section>
      )
      ) : showWorkflow || openId === null ? (
        <WorkflowGraphBoard
          graph={workflow.data.graph}
          cards={data.cards}
          selectedCardId={openId}
          stageFilter={stageFilter}
          onStageFilterChange={setStageFilter}
          onClearSelection={() => open(null)}
          onOpen={(id) => open(id)}
          hasMore={board.hasNextPage}
          {...(data.stageTotals === undefined ? {} : { stageTotals: data.stageTotals })}
          onLoadMore={board.fetchNextPage}
          layoutKey={store.currentTeamId ?? 'default'}
        />
      ) : (
        <section className={`task-workbench${taskChatEligible ? ' task-workbench-chat' : ''}`} aria-label="Tasks">
          <TaskBrowser
            cards={data.cards}
            graph={workflow.data.graph}
            total={totalCardCount}
            {...(data.stageTotals === undefined ? {} : { stageTotals: data.stageTotals })}
            selectedCardId={openId}
            stageFilter={stageFilter}
            onStageFilterChange={setStageFilter}
            hasMore={board.hasNextPage}
            onLoadMore={board.fetchNextPage}
            onOpen={open}
          />
          <div className="task-workbench-detail">
            {selected === null ? (
              <div className="task-workbench-empty"><strong>Select a task</strong><span>Its status, actions, evidence, and updates will appear here.</span></div>
            ) : taskChatEligible ? (
              <TaskChatView card={selected} taskBindingId={taskBindingId} />
            ) : (
              <CardDetail
                card={selected}
                taskBindingId={taskBindingId}
                roleNames={roleNames}
                onClose={() => setShowWorkflow(true)}
                closeLabel="← Workflow"
                onOpenTaskChat={() => void navigate(`/board/${encodeURIComponent(selected.externalItemId)}/chat`)}
                graph={workflow.data.graph}
                humanReviewStageId={workflow.data.states.find((state) => state.kind === 'human-review')?.id ?? 'human-review'}
                onRequestMove={(to) => requestMove(selected, to)}
              />
            )}
          </div>
        </section>
      )}

      {pendingMove === null ? null : (
        <MoveCardDialog
          move={pendingMove}
          question={(decisions.data ?? []).find((d) => d.taskBindingId === pendingMove.id) ?? null}
          busy={move.isPending}
          onConfirm={(comment) => {
            move.mutate({ id: pendingMove.id, to: pendingMove.to, comment });
            setPendingMove(null);
          }}
          onCancel={() => setPendingMove(null)}
        />
      )}

      {workflow.data?.graph != null || graphlessTaskChat ? null : selected === null ? (
        openId === null ? null : <MissingCard id={openId} onClose={() => open(null)} />
      ) : taskChatEligible ? (
        <TaskChatView card={selected} taskBindingId={taskBindingId} />
      ) : (
        <CardDetail
          card={selected}
          taskBindingId={taskBindingId}
          roleNames={roleNames}
          onClose={() => open(null)}
          onOpenTaskChat={() => void navigate(`/board/${encodeURIComponent(selected.externalItemId)}/chat`)}
          graph={workflow.data?.graph ?? null}
          humanReviewStageId={workflow.data?.states.find((state) => state.kind === 'human-review')?.id ?? 'human-review'}
          onRequestMove={(to) => requestMove(selected, to)}
        />
      )}
    </div>
  );
}

export function boardReadPhase(data: unknown, error: unknown): 'blocking' | 'stale' | 'ready' {
  if (data === undefined) return 'blocking';
  return error == null ? 'ready' : 'stale';
}

export function GraphlessTaskBrowser({ cards, total, selectedCardId, hasMore, onLoadMore, onOpen }: {
  cards: readonly BoardCardView[];
  total: number;
  selectedCardId: string | null;
  hasMore: boolean;
  onLoadMore: () => void;
  onOpen: (id: string) => void;
}): ReactNode {
  return (
    <aside className="workflow-task-browser" aria-label="Tasks">
      <header>
        <strong>Tasks</strong>
        <span className="workflow-task-browser-count">{total}</span>
      </header>
      <div className="workflow-task-list">
        {cards.map((card) => (
          <button
            key={card.externalItemId}
            type="button"
            className={`workflow-task-row${card.externalItemId === selectedCardId ? ' selected' : ''}`}
            onClick={() => onOpen(card.externalItemId)}
          >
            <strong>{card.title}</strong>
            <span>{columnTitle(card.workflowState)}</span>
            <time dateTime={card.createdAt}>Created {formatTimestamp(card.createdAt)}</time>
          </button>
        ))}
        {hasMore ? (
          <Button size="small" onClick={onLoadMore}>Load more tasks</Button>
        ) : null}
      </div>
    </aside>
  );
}

function TaskBrowser({ cards, graph, total, stageTotals, selectedCardId, stageFilter, onStageFilterChange, hasMore, onLoadMore, onOpen }: {
  cards: readonly BoardCardView[];
  graph: NonNullable<WorkflowView['graph']>;
  total: number;
  stageTotals?: Readonly<Record<string, number>>;
  selectedCardId: string | null;
  stageFilter: string | null;
  onStageFilterChange: (stageId: string | null) => void;
  hasMore: boolean;
  onLoadMore: () => void;
  onOpen: (id: string) => void;
}): ReactNode {
  const [showCompleted, setShowCompleted] = useState(false);
  const terminal = new Set(graph.nodes.filter((node) => node.kind === 'terminal').map((node) => node.id));
  const projection = projectWorkflowTasks(cards, terminal, stageFilter, showCompleted, selectedCardId, total, stageTotals);
  const pageLoad = workflowTaskPageLoadState({
    loadedCardCount: cards.length,
    visibleCardCount: projection.visible.length,
    expectedVisibleCardCount: projection.expectedVisible,
    hasMore,
    showCompleted,
    filteredTotalIsUnknown: stageFilter !== null && stageTotals === undefined,
  });
  useEffect(() => {
    if (pageLoad.shouldLoad) onLoadMore();
  }, [onLoadMore, pageLoad.pageProgress, pageLoad.shouldLoad]);
  return (
    <aside className="workflow-task-browser">
      <header>
        <select aria-label="Filter tasks by stage" value={stageFilter ?? ''} onChange={(event) => onStageFilterChange(event.currentTarget.value || null)}>
          <option value="">All tasks</option>
          {graph.nodes.map((node) => <option key={node.id} value={node.id}>{node.title}</option>)}
        </select>
        <span className="workflow-task-browser-count">{projection.expectedVisible}</span>
      </header>
      <label className="workflow-task-browser-filter">
        <input type="checkbox" checked={showCompleted} onChange={(event) => setShowCompleted(event.currentTarget.checked)} />
        Show completed {!showCompleted && stageFilter === null ? <span>{projection.hidden} hidden</span> : null}
      </label>
      <div className="workflow-task-list">
        {projection.visible.map((card) => (
          <button key={card.externalItemId} type="button" className={`workflow-task-row${card.externalItemId === selectedCardId ? ' selected' : ''}`} onClick={() => onOpen(card.externalItemId)}>
            <strong>{card.title}</strong>
            <span>{graph.nodes.find((node) => node.id === card.workflowState)?.title ?? columnTitle(card.workflowState)}</span>
            <time dateTime={card.createdAt}>Created {formatTimestamp(card.createdAt)}</time>
          </button>
        ))}
        {showCompleted && hasMore ? (
          <button type="button" className="link-button" onClick={onLoadMore}>Load older completed tasks</button>
        ) : null}
      </div>
    </aside>
  );
}

function NewCard({ columns, onDone }: { columns: readonly string[]; onDone: () => void }): ReactNode {
  const create = useConsoleMutation<Record<string, unknown>, void>(
    (data, context, input) => data.createCard(input as never, context),
    [['board'], ['tasks']],
  );

  return (
    <form
      className="board-new"
      onSubmit={(event) => {
        event.preventDefault();
        const form = new FormData(event.currentTarget);
        const title = String(form.get('title') ?? '').trim();
        if (title === '') return;
        create.mutate(
          {
            title,
            body: String(form.get('body') ?? ''),
            acceptanceCriteria: String(form.get('criteria') ?? '')
              .split('\n')
              .map((line) => line.trim())
              .filter((line) => line.length > 0),
            workflowState: String(form.get('state') ?? columns[0] ?? ''),
            priority: String(form.get('priority') ?? '') || null,
            labels: [],
            managed: form.get('managed') !== null,
          },
          { onSuccess: onDone },
        );
      }}
    >
      <h2>New card</h2>

      <label className="field-label" htmlFor="new-title">
        What needs doing
      </label>
      <input id="new-title" name="title" required autoFocus />

      <label className="field-label" htmlFor="new-body">
        Context
      </label>
      <textarea id="new-body" name="body" rows={3} placeholder="What you would tell a colleague before they started" />

      <label className="field-label" htmlFor="new-criteria">
        Done when
      </label>
      <textarea
        id="new-criteria"
        name="criteria"
        rows={3}
        placeholder="One per line. Each has to be something you can check."
      />

      <div className="board-new-row">
        <span className="field">
          <label className="field-label" htmlFor="new-state">
            Column
          </label>
          <select id="new-state" name="state">
            {columns.map((column) => (
              <option key={column} value={column}>
                {column}
              </option>
            ))}
          </select>
        </span>
        <span className="field">
          <label className="field-label" htmlFor="new-priority">
            Priority
          </label>
          <select id="new-priority" name="priority" defaultValue="P1">
            {['P0', 'P1', 'P2', 'P3'].map((priority) => (
              <option key={priority} value={priority}>
                {priority}
              </option>
            ))}
          </select>
        </span>
      </div>

      <label className="board-new-managed">
        <input type="checkbox" name="managed" defaultChecked />
        Agents may pick this up
      </label>

      <div className="board-new-actions">
        <Button variant="primary" type="submit" disabled={create.isPending}>
          {create.isPending ? 'Opening…' : 'Open card'}
        </Button>
        <Button onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function authorName(comment: BoardCommentView, roleNames: ReadonlyMap<string, string>): string {
  if (comment.authorType === 'human') return comment.authorId;
  if (comment.authorId === 'workflow') return 'the workflow';
  const named = roleNames.get(comment.authorId);
  if (named !== undefined) return named;
  // A role instance from a retired team version has no name to resolve.
  // «agent» reads better than a UUID nobody can act on.
  return /^[0-9a-f]{8}-[0-9a-f]{4}-/u.test(comment.authorId) ? 'agent' : comment.authorId;
}

function phaseLabel(phase: NonNullable<BoardCardView['stagePosition']>['phase']): string {
  if (phase === 'in_progress') return 'In progress';
  if (phase === 'done') return 'Done';
  return 'In queue';
}

function eligibilityLabel(eligibility: NonNullable<BoardCardView['stagePosition']>['eligibility']): string {
  if (eligibility === 'dependency_blocked') return 'Blocked by dependency';
  if (eligibility === 'paused') return 'Waiting for a person';
  return 'Ready';
}

/** The last path segment of a repository URL — «backend» from «…/reachgoals-tribe/backend». */
function repoShort(url: string): string {
  const trimmed = url.replace(/\/+$/, '');
  const last = trimmed.slice(trimmed.lastIndexOf('/') + 1);
  return last === '' ? url : last.replace(/\.git$/, '');
}

/** «!497» for a merge request, «#497» for a pull request — the host's own shorthand. */
function changeRef(change: BoardChangeLinkView): string {
  return `${change.kind === 'merge_request' ? '!' : '#'}${change.externalId}`;
}

/**
 * The team's latest substantive word on the card — the newest agent comment
 * that is an actual report, not bookkeeping (claims, moves, attach notes).
 * This is what the Evidence block pins above the discussion: the conclusion
 * a person needs first, without scrolling through the whole exchange.
 */
function latestVerdict(card: BoardCardView): BoardCommentView | null {
  for (let index = card.comments.length - 1; index >= 0; index -= 1) {
    const entry = card.comments[index];
    if (entry === undefined || entry.authorType === 'human' || entry.kind === 'claim') continue;
    const body = withoutAutomationMarker(entry.body);
    if (/^moved to [\w-]+/u.test(body) || /^attached .+$/u.test(body)) continue;
    return entry;
  }
  return null;
}

/**
 * The two places an attachment appears, each wired to the same fetch.
 *
 * Separate components because a hook cannot be called conditionally: the dialog
 * exists only while something is open, and the tile only while its comment is
 * on screen.
 */
function Tile({ attachment, onOpen }: { attachment: BoardAttachmentView; onOpen: () => void }): ReactNode {
  return <AttachmentTile attachment={attachment} loaded={useAttachment(attachment)} onOpen={onOpen} />;
}

function Dialog({
  attachment,
  siblings,
  onSelect,
  onClose,
}: {
  attachment: BoardAttachmentView;
  /** Every attachment on the card, in card order — what the arrows step through. */
  siblings: readonly BoardAttachmentView[];
  onSelect: (next: BoardAttachmentView) => void;
  onClose: () => void;
}): ReactNode {
  const index = siblings.findIndex((candidate) => candidate.id === attachment.id);
  const prev = index > 0 ? siblings[index - 1] : undefined;
  const next = index >= 0 && index < siblings.length - 1 ? siblings[index + 1] : undefined;
  return (
    <AttachmentDialog
      attachment={attachment}
      loaded={useAttachment(attachment)}
      onClose={onClose}
      position={index === -1 ? undefined : { index, total: siblings.length }}
      onPrev={prev === undefined ? undefined : () => onSelect(prev)}
      onNext={next === undefined ? undefined : () => onSelect(next)}
    />
  );
}

/** A link named a card the board no longer has. Say so; do not render nothing. */
function MissingCard({ id, onClose }: { id: string; onClose: () => void }): ReactNode {
  return (
    <section className="card-detail" aria-label="Card not found">
      <header className="card-detail-head">
        <h2 className="card-detail-title">No card with that id</h2>
        <Button onClick={onClose}>Close</Button>
      </header>
      <p className="card-detail-empty">
        <code>{id}</code> is not on this board. It may have been deleted, or it may live on a different project.
      </p>
    </section>
  );
}

export function TaskChatPanel({ taskBindingId, onOpen }: {
  taskBindingId: string | null;
  onOpen: () => void;
}): ReactNode {
  if (taskBindingId === null) return null;
  return (
    <section className="task-chat-launch" aria-label="Task Chat">
      <div>
        <h3>Task Chat</h3>
        <p>Talk to the Main Operator about this task.</p>
      </div>
      <Button onClick={onOpen}>Chat with Task</Button>
    </section>
  );
}

export function TaskChatHeader({ cardId, title, workflowState, returnStageId, humanReviewReason }: {
  cardId: string;
  title: string;
  workflowState: string;
  returnStageId?: string | null;
  humanReviewReason?: 'blocked_on_human' | null;
}): ReactNode {
  const waitingOnHuman = humanReviewReason === 'blocked_on_human';
  return (
    <header className="task-chat-head">
      <div>
        <p className="task-chat-kicker">Task Chat</p>
        <h2 tabIndex={-1}>{title}</h2>
        <p className="card-detail-facts">
          <span>{columnTitle(workflowState)}</span>
          {waitingOnHuman && returnStageId ? <span>Waiting for human input · return to: {columnTitle(returnStageId)}</span> : null}
          <span className="card-detail-id">{cardId}</span>
        </p>
      </div>
      <Button as={Link} to={`/board/${encodeURIComponent(cardId)}`}>Back to task details</Button>
    </header>
  );
}

function TaskChatView({ card, taskBindingId }: { card: BoardCardView; taskBindingId: string }): ReactNode {
  const viewRef = useRef<HTMLElement>(null);
  const store = useStore();
  useEffect(() => {
    if (globalThis.matchMedia?.('(max-width: 1100px)').matches === true) {
      viewRef.current?.scrollIntoView({ behavior: 'auto', block: 'start' });
    }
    viewRef.current?.querySelector('h2')?.focus({ preventScroll: true });
    store.announce(`Opened Task Chat for ${card.title}`);
  }, [card.externalItemId, card.title, store]);
  return (
    <section className="task-chat-view" aria-label={`Task Chat for ${card.title}`} ref={viewRef}>
      <TaskChatHeader
        cardId={card.externalItemId}
        title={card.title}
        workflowState={card.workflowState}
        {...(card.stagePosition?.returnStageId === undefined ? {} : { returnStageId: card.stagePosition.returnStageId })}
        {...(card.humanReviewReason === undefined ? {} : { humanReviewReason: card.humanReviewReason })}
      />
      <TaskConversation key={taskBindingId} taskBindingId={taskBindingId} />
    </section>
  );
}

function CardDetail({
  card,
  taskBindingId,
  roleNames,
  onClose,
  onOpenTaskChat,
  closeLabel = 'Close',
  graph,
  humanReviewStageId,
  onRequestMove,
}: {
  card: BoardCardView;
  /** Null until the connector has bound this card to a task, and for unmanaged cards. */
  taskBindingId: string | null;
  roleNames: ReadonlyMap<string, string>;
  onClose: () => void;
  onOpenTaskChat: () => void;
  closeLabel?: string;
  graph: WorkflowView['graph'];
  humanReviewStageId: string;
  onRequestMove: (to: string) => void;
}): ReactNode {
  const [viewing, setViewing] = useState<BoardAttachmentView | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const store = useStore();

  /*
   * An opened card takes focus and scrolls to itself.
   *
   * It renders below a board that can be taller than the window, so without
   * this the click appears to do nothing at all. Focus moves with it rather
   * than staying on a card in the column above, which is also what makes the
   * next Tab land inside the card that just opened (§16.5).
   */
  useEffect(() => {
    const workbench = headingRef.current?.closest('.task-workbench-detail');
    if (workbench === null || workbench === undefined) headingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    else workbench.scrollTo({ top: 0, behavior: 'auto' });
    headingRef.current?.focus({ preventScroll: true });
    store.announce(`Opened ${card.title}`);
  }, [card.externalItemId, card.title, store]);

  /*
   * The files go as files (ADR-119).
   *
   * Each one used to be read into a data URL, split at the comma and sent as a
   * base64 string: a 5 MB screenshot became 6.7 MB of JSON, held in memory
   * twice, and nothing could be sent until every file had been read. `FormData`
   * hands the browser the `File` objects and it streams them.
   */
  const comment = useConsoleMutation<CommentDraft, void>(
    (data, context, draft) => data.commentOnCard(card.externalItemId, draft.body, context, draft.files),
    [['board']],
  );

  const verdict = latestVerdict(card);
  const currentStage = graph?.nodes.find((stage) => stage.id === card.workflowState) ?? null;
  const stageActions = graph?.edges.filter((edge) => edge.from === card.workflowState) ?? [];
  const completedStage = graph?.edges.find(
    (edge) => edge.from === card.workflowState && edge.outcome === 'complete'
      && graph.nodes.some((stage) => stage.id === edge.to && stage.kind === 'terminal'),
  )?.to ?? null;
  const hasConfiguredCancellation = stageActions.some((edge) => edge.to === graph?.cancelledStage);
  const humanOnly = card.executionPolicy === 'human';
  const isHumanReview = currentStage?.id === humanReviewStageId;
  const isHumanTesting = currentStage?.id === 'human-testing';
  const humanTestingPaused = card.stagePosition?.eligibility === 'paused';
  const acceptanceAction = isHumanTesting && !humanTestingPaused
    ? stageActions.find((edge) => edge.to === 'ready-to-deploy') ?? null
    : null;
  const feedbackComments = card.comments.filter((entry) => entry.body.startsWith('[agent-feedback]'));
  const substantiveComments = card.comments.filter((entry) => !isTechnicalComment(entry) && !entry.body.startsWith('[agent-feedback]'));
  const technicalComments = card.comments.filter(isTechnicalComment);

  const summaryItems = (entries: readonly BoardCommentView[]): ReactNode => entries.map((entry) => {
    const who = authorName(entry, roleNames);
    const displayed = entry.body.startsWith('[agent-feedback]')
      ? { ...entry, body: entry.body.slice('[agent-feedback]'.length).trimStart() }
      : entry;
    return (
      <li key={entry.id}>
        <details>
          <summary>
            <span className="task-summary-author">{who}</span>
            <span className="task-summary-copy">{commentSummary(displayed, who)}</span>
            <time dateTime={entry.createdAt}>{formatTimestamp(entry.createdAt)}</time>
          </summary>
          <div className="task-summary-original">
            <span>Original comment</span>
            <p>{withoutAutomationMarker(displayed.body)}</p>
          </div>
        </details>
      </li>
    );
  });

  return (
    <section className="card-detail" aria-label={card.title}>
      <header className="card-detail-head">
        <h2 className="card-detail-title" tabIndex={-1} ref={headingRef}>
          {card.title}
        </h2>
        <Button onClick={onClose}>{closeLabel}</Button>
      </header>

      <p className="card-detail-facts">
        <span className="card-detail-state">{currentStage?.title ?? columnTitle(card.workflowState)}</span>
        {card.humanReviewReason === 'blocked_on_human' && card.stagePosition?.returnStageId !== null && card.stagePosition?.returnStageId !== undefined ? (
          <span>Waiting for human input · return to {columnTitle(card.stagePosition.returnStageId)}</span>
        ) : null}
        {card.stagePosition === null || card.stagePosition === undefined ? null : (
          <span>{phaseLabel(card.stagePosition.phase)} · {eligibilityLabel(card.stagePosition.eligibility)}</span>
        )}
        {card.priority === null ? null : <Chip tone={PRIORITY_TONES[card.priority] ?? 'unknown'} text={PRIORITY_LABELS[card.priority] ?? card.priority} />}
        <span className="card-detail-id">{card.externalItemId}</span>
        {card.managed ? null : <span className="card-detail-unmanaged">No agent will take this</span>}
      </p>

      <TaskChatPanel taskBindingId={card.managed ? taskBindingId : null} onOpen={onOpenTaskChat} />

      {isHumanReview || humanOnly || card.stagePosition?.eligibility !== 'paused' ? null : (
        <section className="task-attention" aria-label="Action required">
          <div>
            <span className="task-overview-kicker">Action required</span>
            <strong>Automation is paused and this task needs a human decision.</strong>
          </div>
          {graph === null ? null : (
            <div className="task-attention-actions">
              {card.workflowState === graph.cancelledStage ? null : (
                <Button variant="danger" onClick={() => onRequestMove(graph.cancelledStage)}>Cancel task</Button>
              )}
            </div>
          )}
        </section>
      )}

      {graph === null || (!humanOnly && card.stagePosition?.eligibility === 'paused' && !isHumanReview) ? null : (
        <div className="card-detail-stage-actions" aria-label="Task workflow actions">
          {humanOnly && currentStage?.kind !== 'human' && completedStage !== null ? (
            <Button variant="primary" onClick={() => onRequestMove(completedStage)}>Complete</Button>
          ) : null}
          {isHumanTesting ? (
            <>
              {acceptanceAction === null ? null : (
                <Button variant="primary" onClick={() => onRequestMove(acceptanceAction.to)}>
                  {acceptanceAction.title || 'Move to Ready to Deploy'}
                </Button>
              )}
              {card.workflowState === graph.cancelledStage ? null : (
                <Button variant="danger" onClick={() => onRequestMove(graph.cancelledStage)}>Cancel task…</Button>
              )}
            </>
          ) : isHumanReview || currentStage?.id === 'ready-to-deploy' ? (
            card.workflowState === graph.cancelledStage ? null : (
              <Button variant="danger" onClick={() => onRequestMove(graph.cancelledStage)}>Cancel task…</Button>
            )
          ) : currentStage?.kind === 'human' ? stageActions.map((edge) => (
            <Button
              key={edge.id}
              variant={edge.to === graph.cancelledStage ? 'danger' : 'primary'}
              onClick={() => onRequestMove(edge.to)}
            >
              {edge.title || columnTitle(edge.to)}{edge.to === graph.cancelledStage ? '…' : ''}
            </Button>
          )) : null}
          {isHumanReview || isHumanTesting || currentStage?.id === 'ready-to-deploy' || card.workflowState === graph.cancelledStage || hasConfiguredCancellation ? null : (
            <Button variant="danger" onClick={() => onRequestMove(graph.cancelledStage)}>Cancel task…</Button>
          )}
        </div>
      )}

      <AgentCardLabels labels={card.agentLabels} />

      <CardReleaseAction card={card} />

      {verdict === null && card.attachments.length === 0 ? null : (
        <details className="task-detail-section task-evidence-primary" open>
          <summary>Files and evidence <span>{card.attachments.length}</span></summary>
          <div className="task-detail-section-body evidence">
            {verdict === null ? null : <p>{commentSummary(verdict, authorName(verdict, roleNames))}</p>}
            <BeforeAfterCompare attachments={card.attachments} />
            <ul className="attachment-list">{card.attachments.map((attachment) => <li key={attachment.id}><Tile attachment={attachment} onOpen={() => setViewing(attachment)} /></li>)}</ul>
          </div>
        </details>
      )}

      <section className="task-overview-card">
        <span className="task-overview-kicker">Delivery</span>
        {card.changeLinks.length === 0 ? <p>No merge request linked yet.</p> : (
          <div className="task-overview-links">{card.changeLinks.map((change) => {
            const props = externalReleaseLinkProps(change.url);
            const label = `${repoShort(change.repositoryUrl)} ${changeRef(change)}`;
            return props === null ? <span key={change.url}>{label}</span> : <a key={change.url} {...props}>{label}</a>;
          })}</div>
        )}
        {taskBindingId === null ? null : <ChecklistSummary taskBindingId={taskBindingId} />}
      </section>

      <div className="task-detail-navigation" aria-label="Task details">
        <details className="task-detail-section">
          <summary>Full requirements</summary>
          <div className="task-detail-section-body">
            {card.body.trim() === '' ? <p className="card-detail-empty">No description was written.</p> : <p className="card-detail-body">{card.body}</p>}
            <h4>Done when</h4>
            {card.acceptanceCriteria.length === 0 ? <p className="card-detail-warn">No acceptance criteria.</p> : (
              <ol className="criteria">{card.acceptanceCriteria.map((criterion) => <li key={criterion}>{criterion}</li>)}</ol>
            )}
          </div>
        </details>

        {taskBindingId === null && technicalComments.length === 0 ? null : (
          <details className="task-detail-section">
            <summary>Technical details</summary>
            <div className="task-detail-section-body">
              {taskBindingId === null ? null : <><ChecklistProgress taskBindingId={taskBindingId} /><div className="card-detail-runs"><RunTimeline taskBindingId={taskBindingId} /></div></>}
              {technicalComments.length === 0 ? null : (
                <ul className="entry-list task-technical-list">{technicalComments.map((entry) => {
                  const who = authorName(entry, roleNames);
                  return <li className="entry" key={entry.id}><div className="entry-meta"><span>{who}</span><time dateTime={entry.createdAt}>{formatTimestamp(entry.createdAt)}</time></div><p>{describeTechnicalComment(entry, who)}</p></li>;
                })}</ul>
              )}
            </div>
          </details>
        )}
      </div>

      {Array.isArray(card.releaseOperations) && card.releaseOperations.length > 0 ? (
        <>
          <h3 className="card-detail-label">Deployments and releases</h3>
          <BoardReleaseOperations operations={card.releaseOperations} compact={false} />
        </>
      ) : null}

      <details className="task-older-history">
        <summary>Change Log <span>{substantiveComments.length}</span></summary>
        {substantiveComments.length === 0 ? (
          <p className="card-detail-empty">Nothing has happened to this card yet.</p>
        ) : (
          <ol className="task-summary-list">{summaryItems(substantiveComments)}</ol>
        )}
      </details>

      <details className="task-agent-feedback">
        <summary>Report an agent issue {feedbackComments.length === 0 ? null : <span>{feedbackComments.length}</span>}</summary>
        <p>Record what the agent missed or did incorrectly. Add screenshots or files so this case can be replayed and used to improve the team.</p>
        {feedbackComments.length === 0 ? null : (
          <ol className="task-summary-list">{summaryItems(feedbackComments)}</ol>
        )}
        <AgentFeedbackComposer cardId={card.externalItemId} />
      </details>

      <CommentComposer
        pending={comment.isPending}
        error={comment.error === null ? null : comment.error.message}
        onSubmit={(draft) => comment.mutate(draft)}
      />


      {/*
        Keyed, so opening a second file is a fresh dialog rather than the first
        one with new props — which also resets the page number and the zoom,
        because inheriting page 7 when you open a different document is wrong.
      */}
      {viewing === null ? null : (
        <Dialog attachment={viewing} siblings={card.attachments} onSelect={setViewing} onClose={() => setViewing(null)} />
      )}
    </section>
  );
}

function AgentFeedbackComposer({ cardId }: { cardId: string }): ReactNode {
  const feedback = useConsoleMutation<CommentDraft, void>(
    (data, context, draft) => data.commentOnCard(cardId, `[agent-feedback] ${draft.body}`, context, draft.files),
    [['board']],
  );
  return <CommentComposer pending={feedback.isPending} error={feedback.error?.message ?? null} onSubmit={(draft) => feedback.mutate(draft)} />;
}

function CardReleaseAction({ card }: { readonly card: BoardCardView }): ReactNode {
  const plans = useReleasePlans();
  const configuration = useReleaseConfiguration();
  const [selected, setSelected] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const add = useConsoleMutation(
    (data, context, args: { releaseId: string; card: string }) => data.addReleaseTask(args.releaseId, args.card, context),
    [['release-plans'], ['board']],
  );
  const createAndAdd = useConsoleMutation(async (data, context, args: { title: string; card: string }) => {
    await data.createReleasePlan(args.title, context, [args.card]);
  }, [['release-plans'], ['board']]);
  const membership = plans.data?.find((detail) => detail.release.status === 'DRAFT' && detail.tasks.some((task) => task.removedAt === null && task.externalItemId === card.externalItemId)) ?? null;
  const eligible = card.stagePosition != null && card.stagePosition.phase !== 'done' && configuration.data?.environments.some((environment) =>
    (environment.readyStages ?? (configuration.data.readyStage === null ? [] : [configuration.data.readyStage])).includes(card.stagePosition!.stageId));
  if (!eligible && membership === null) return null;
  if (membership !== null) {
    return <section className="card-release-membership"><span>Release</span><strong>{membership.release.title}</strong><span>Revision {membership.release.revision}</span><Button as={Link} to="/releases?view=drafts">Open release</Button></section>;
  }
  const drafts = plans.data?.filter((detail) => detail.release.status === 'DRAFT' && detail.release.pendingMessageKey === null) ?? [];
  return <section className="card-release-action" aria-label="Add to release">
    <div><span className="task-overview-kicker">Release</span><strong>Add this task to a named release</strong></div>
    <div className="card-release-controls">
      <select aria-label="Release" value={selected} onChange={(event) => setSelected(event.target.value)}><option value="">Select release…</option>{drafts.map((detail) => <option key={detail.release.id} value={detail.release.id}>{detail.release.title}</option>)}</select>
      <Button variant="primary" disabled={!selected || add.isPending} onClick={() => add.mutate({ releaseId: selected, card: card.externalItemId })}>Add to release</Button>
    </div>
    <details><summary>Create a new release</summary><div className="card-release-controls"><input value={newTitle} placeholder="Release name" onChange={(event) => setNewTitle(event.target.value)} /><Button disabled={!newTitle.trim() || createAndAdd.isPending} onClick={() => createAndAdd.mutate({ title: newTitle.trim(), card: card.externalItemId })}>Create and add</Button></div></details>
  </section>;
}

function ChecklistProgress({ taskBindingId }: { readonly taskBindingId: string }): ReactNode {
  const query = useTaskChecklists(taskBindingId);
  if (query.isPending || query.error !== null || (query.data?.length ?? 0) === 0) return null;
  return (
    <>
      <h3 className="card-detail-label">Checklists</h3>
      <div className="task-checklists">
        {(query.data ?? []).map((checklist) => {
          const latest = new Map<string, (typeof checklist.attempts)[number]>();
          for (const attempt of checklist.attempts) latest.set(attempt.itemId, attempt);
          return (
            <article className="task-checklist" key={checklist.id}>
              <header><strong>{checklist.snapshot.title}</strong><Chip tone={checklist.state === 'passed' ? 'active' : checklist.state === 'open' ? 'info' : 'danger'} text={checklist.state} /></header>
              {checklist.snapshot.description === '' ? null : <p>{checklist.snapshot.description}</p>}
              <ol>
                {checklist.snapshot.items.map((item) => {
                  const attempt = latest.get(item.id);
                  return <li key={item.id} className={`checklist-item checklist-item-${attempt?.verdict ?? 'pending'}`}>
                    <div><strong>{item.title}</strong><span>{attempt?.verdict ?? 'pending'}</span></div>
                    {item.description === '' ? null : <p>{item.description}</p>}
                    {attempt === undefined ? null : <><p className="checklist-result">{attempt.result}</p><small>{attempt.executionMode === 'child' ? `Delegated · ${attempt.reporterId}` : attempt.reporterId}</small></>}
                    {attempt?.artifacts.length ? <ul className="checklist-artifacts">{attempt.artifacts.map((artifact) => <li key={`${attempt.id}:${artifact.requirementId}:${artifact.ref}`}><span>{artifact.requirementId}</span><code>{artifact.ref}</code></li>)}</ul> : null}
                  </li>;
                })}
              </ol>
            </article>
          );
        })}
      </div>
    </>
  );
}

function ChecklistSummary({ taskBindingId }: { readonly taskBindingId: string }): ReactNode {
  const query = useTaskChecklists(taskBindingId);
  if (query.isPending) return <small>Checks loading…</small>;
  if (query.error !== null) return <small>Checks unavailable</small>;
  const checklists = query.data ?? [];
  if (checklists.length === 0) return <small>No checklist assigned.</small>;

  let passed = 0;
  let failed = 0;
  let pending = 0;
  for (const checklist of checklists) {
    const latest = new Map<string, (typeof checklist.attempts)[number]>();
    for (const attempt of checklist.attempts) latest.set(attempt.itemId, attempt);
    for (const item of checklist.snapshot.items) {
      const verdict = latest.get(item.id)?.verdict;
      if (verdict === 'passed') passed += 1;
      else if (verdict === 'failed' || verdict === 'blocked') failed += 1;
      else pending += 1;
    }
  }

  return <div className="task-check-summary" aria-label="Checklist summary">
    <span className="task-check-passed">{passed} passed</span>
    {failed === 0 ? null : <span className="task-check-failed">{failed} failed</span>}
    {pending === 0 ? null : <span>{pending} pending</span>}
  </div>;
}

/**
 * Moving a card costs words — the same rule the agents live under.
 *
 * With an open question the dialog shows it, and the comment is the answer;
 * without one the comment simply says why. Either way the words land on the
 * card first, then the card moves — a silent drag is the one thing this
 * dialog exists to prevent.
 */
function MoveCardDialog({
  move,
  question,
  busy,
  onConfirm,
  onCancel,
}: {
  readonly move: { id: string; title: string; to: string };
  readonly question: Pick<HumanReviewRequestView, 'requestedByRoleInstanceId' | 'question'> | null;
  readonly busy: boolean;
  readonly onConfirm: (comment: string) => void;
  readonly onCancel: () => void;
}): ReactNode {
  const [comment, setComment] = useState('');
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  return (
    <dialog className="move-dialog" ref={dialogRef} onClose={onCancel} onCancel={onCancel}>
      <h3 className="move-dialog-title">
        Move «{move.title}» to {columnTitle(move.to)}
      </h3>
      {question === null ? (
        <p className="move-dialog-lede">Say why — the comment lands on the card, then the card moves.</p>
      ) : (
        <>
          <p className="move-dialog-question">
            <strong>{question.requestedByRoleInstanceId}</strong> asks: {question.question}
          </p>
          <p className="move-dialog-lede">Your comment answers this — it lands on the card, then the card moves.</p>
        </>
      )}
      <textarea
        className="move-dialog-comment"
        rows={3}
        autoFocus
        placeholder={question === null ? 'Why is it moving?' : 'Your answer…'}
        value={comment}
        onChange={(event) => setComment(event.target.value)}
      />
      <div className="move-dialog-actions">
        <Button onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button variant="primary" disabled={busy || comment.trim().length === 0} onClick={() => onConfirm(comment.trim())}>
          Comment and move
        </Button>
      </div>
    </dialog>
  );
}
